/**
 * @file Static collision. A uniform bucket grid of AABBs and circles with
 * circle-vs-world resolution and sliding — deliberately the cheapest thing that
 * feels right for a top-down game. No physics engine, no rotation, no stacking.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * HOW IT FEELS
 * ─────────────────────────────────────────────────────────────────────────────
 * `resolveCircle` pushes the actor out along the LEAST-PENETRATION axis and does
 * NOT touch the tangential component of its movement. That is the whole trick:
 * walking into a wall at 45 degrees leaves the along-wall velocity intact, so
 * the player slides instead of sticking. Two iterations resolve corners (get
 * pushed out of A, land in B, get pushed out of B) without the jitter a single
 * pass produces.
 *
 * Vertical extents (`y`/`h` on {@link Collider}) are STORED but not used for
 * resolution — this is a 2D solver. Overpasses/bridges need the caller to swap
 * grids or filter by tag.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * MOSTLY STATIC, AND THE ONE EXCEPTION
 * ─────────────────────────────────────────────────────────────────────────────
 * The grid is built once and read forever, which is why buckets are plain
 * arrays of ids. Two shapes of churn are supported anyway, and only these two:
 * {@link CollisionGrid#setShapeEnabled} flips a shape out of every query
 * without touching a bucket, and {@link CollisionGrid#setCircle} re-buckets one
 * circle. Both exist for PARKED CARS (`engine/traffic/Traffic.js`) — a car the
 * player abandons is world geometry from that moment, and one they drive off in
 * must stop being a wall. Neither is a per-frame call: `setCircle` splices the
 * old buckets, which is fine at a handful of events and wrong at 60 Hz. Moving
 * actors do not belong here.
 *
 * @module engine/world/CollisionGrid
 */

/** @typedef {import('../types.js').Bounds2} Bounds2 */
/** @typedef {import('../types.js').Collider} Collider */
/** @typedef {import('../types.js').ColliderTag} ColliderTag */

const KIND_AABB = 0;
const KIND_CIRCLE = 1;

export class CollisionGrid {
  /**
   * @param {Bounds2} bounds Playable extent. Shapes outside it still work —
   *   they clamp into the edge buckets — but keep them rare.
   * @param {number} [cell=4] Bucket size in metres. 4 is tuned for city props
   *   (a building spans a few buckets, a barrel one).
   */
  constructor(bounds, cell = 4) {
    this.bounds = bounds;
    this.cell = cell > 0 ? cell : 4;
    this.minX = bounds.minX;
    this.minZ = bounds.minZ;
    this.nx = Math.max(1, Math.ceil((bounds.maxX - bounds.minX) / this.cell));
    this.nz = Math.max(1, Math.ceil((bounds.maxZ - bounds.minZ) / this.cell));

    /**
     * Flat shape store — parallel arrays, not objects, because `resolveCircle`
     * runs per actor per frame.
     * `kind` 0 = AABB (a,b,c,d = x0,z0,x1,z1); 1 = circle (a,b,c = x,z,r).
     * @type {{kind: Uint8Array, a: Float32Array, b: Float32Array, c: Float32Array, d: Float32Array, y: Float32Array, h: Float32Array, tag: Array<ColliderTag|undefined>, n: number}}
     */
    this.shapes = {
      kind: new Uint8Array(64),
      a: new Float32Array(64),
      b: new Float32Array(64),
      c: new Float32Array(64),
      d: new Float32Array(64),
      y: new Float32Array(64),
      h: new Float32Array(64),
      /** 1 = participates in queries. See {@link CollisionGrid#setShapeEnabled}. */
      on: new Uint8Array(64),
      tag: new Array(64),
      n: 0,
    };

    /** @type {Array<number[]|undefined>} bucket -> shape ids */
    this.buckets = new Array(this.nx * this.nz);

    /** Visit stamps so a shape spanning many buckets is tested once per query. */
    this._stamp = new Int32Array(64);
    this._tick = 0;
  }

  // ── building ───────────────────────────────────────────────────────────────

  /**
   * @param {number} x0 @param {number} z0 @param {number} x1 @param {number} z1
   * @param {ColliderTag} [tag]
   * @param {{ y?: number, h?: number }} [opts]
   * @returns {number} Shape id.
   */
  addAABB(x0, z0, x1, z1, tag, opts) {
    const id = this._alloc();
    const s = this.shapes;
    s.kind[id] = KIND_AABB;
    s.a[id] = Math.min(x0, x1);
    s.b[id] = Math.min(z0, z1);
    s.c[id] = Math.max(x0, x1);
    s.d[id] = Math.max(z0, z1);
    s.y[id] = opts?.y ?? 0;
    s.h[id] = opts?.h ?? Infinity;
    s.tag[id] = tag;
    this._bucketize(id, s.a[id], s.b[id], s.c[id], s.d[id]);
    return id;
  }

  /**
   * @param {number} x @param {number} z @param {number} r
   * @param {ColliderTag} [tag]
   * @param {{ y?: number, h?: number }} [opts]
   * @returns {number} Shape id.
   */
  addCircle(x, z, r, tag, opts) {
    const id = this._alloc();
    const s = this.shapes;
    s.kind[id] = KIND_CIRCLE;
    s.a[id] = x;
    s.b[id] = z;
    s.c[id] = r;
    s.d[id] = 0;
    s.y[id] = opts?.y ?? 0;
    s.h[id] = opts?.h ?? Infinity;
    s.tag[id] = tag;
    this._bucketize(id, x - r, z - r, x + r, z + r);
    return id;
  }

  /**
   * Bulk-loads the `Level.colliders` array. (Extension beyond the T3 spec, but
   * every level ends up writing this loop otherwise.)
   * @param {Collider[]} colliders
   * @returns {this}
   */
  addColliders(colliders) {
    for (const c of colliders) {
      if (!c) continue;
      if (c.kind === 'circle') this.addCircle(c.x, c.z, c.r, c.tag, { y: c.y, h: c.h });
      else this.addAABB(c.minX, c.minZ, c.maxX, c.maxZ, c.tag, { y: c.y, h: c.h });
    }
    return this;
  }

  // ── dynamic shapes (parked cars only — see the header) ─────────────────────

  /**
   * Includes or excludes one shape from every query. O(1): the shape keeps its
   * buckets, `_query` just stops emitting it.
   * @param {number} id From {@link addAABB} / {@link addCircle}.
   * @param {boolean} on
   */
  setShapeEnabled(id, on) {
    if (id >= 0 && id < this.shapes.n) this.shapes.on[id] = on ? 1 : 0;
  }

  /** @param {number} id @returns {boolean} */
  isShapeEnabled(id) {
    return id >= 0 && id < this.shapes.n && this.shapes.on[id] === 1;
  }

  /**
   * Moves/resizes an existing CIRCLE, re-bucketing it. Not for per-frame use.
   * @param {number} id @param {number} x @param {number} z @param {number} [r]
   */
  setCircle(id, x, z, r) {
    const s = this.shapes;
    if (!(id >= 0 && id < s.n) || s.kind[id] !== KIND_CIRCLE) return;
    const rr = r ?? s.c[id];
    this._unbucketize(id, s.a[id] - s.c[id], s.b[id] - s.c[id], s.a[id] + s.c[id], s.b[id] + s.c[id]);
    s.a[id] = x;
    s.b[id] = z;
    s.c[id] = rr;
    this._bucketize(id, x - rr, z - rr, x + rr, z + rr);
  }

  // ── queries ────────────────────────────────────────────────────────────────

  /**
   * Pushes a circle out of everything it overlaps, preserving tangential motion
   * (= sliding). Does NOT modify `pos`.
   *
   * @param {{x: number, z: number}} pos Desired position (post-integration).
   * @param {number} r Actor radius (player: 0.35).
   * @param {number} [iterations=2] 2 handles corners; 1 is enough for open
   *   ground; 3+ is wasted.
   * @returns {{x: number, z: number, hit: boolean, nx: number, nz: number}}
   *   Corrected position plus the accumulated (normalised) push direction, which
   *   is what a "bumped into a wall" reaction/footstep hook wants.
   */
  resolveCircle(pos, r, iterations = 2) {
    let x = pos.x;
    let z = pos.z;
    let hit = false;
    let anx = 0;
    let anz = 0;

    for (let iter = 0; iter < iterations; iter++) {
      let px = 0;
      let pz = 0;
      let any = false;

      const ids = this._query(x - r, z - r, x + r, z + r);
      const s = this.shapes;
      for (let k = 0; k < ids.length; k++) {
        const id = ids[k];
        if (s.kind[id] === KIND_AABB) {
          const x0 = s.a[id];
          const z0 = s.b[id];
          const x1 = s.c[id];
          const z1 = s.d[id];
          // Inclusive: a centre sitting EXACTLY on a face has zero distance to
          // the box, so the closest-point path below cannot give it a direction.
          // Treat it as inside and let the least-penetration axis decide.
          const inside = x >= x0 && x <= x1 && z >= z0 && z <= z1;
          if (inside) {
            // Centre is in the box: leave by the nearest face.
            const dl = x - x0;
            const dr = x1 - x;
            const dd = z - z0;
            const du = z1 - z;
            const m = Math.min(dl, dr, dd, du);
            if (m === dl) px -= dl + r;
            else if (m === dr) px += dr + r;
            else if (m === dd) pz -= dd + r;
            else pz += du + r;
            any = true;
            continue;
          }
          const cx = x < x0 ? x0 : x > x1 ? x1 : x;
          const cz = z < z0 ? z0 : z > z1 ? z1 : z;
          const dx = x - cx;
          const dz = z - cz;
          const d2 = dx * dx + dz * dz;
          if (d2 >= r * r || d2 === 0) continue;
          const d = Math.sqrt(d2);
          const push = r - d;
          px += (dx / d) * push;
          pz += (dz / d) * push;
          any = true;
        } else {
          const dx = x - s.a[id];
          const dz = z - s.b[id];
          const rr = r + s.c[id];
          const d2 = dx * dx + dz * dz;
          if (d2 >= rr * rr) continue;
          if (d2 < 1e-12) {
            // Exactly concentric: pick a stable axis rather than NaN.
            px += rr;
            any = true;
            continue;
          }
          const d = Math.sqrt(d2);
          const push = rr - d;
          px += (dx / d) * push;
          pz += (dz / d) * push;
          any = true;
        }
      }

      if (!any) break;
      x += px;
      z += pz;
      anx += px;
      anz += pz;
      hit = true;
    }

    const l = Math.hypot(anx, anz);
    return { x, z, hit, nx: l > 1e-9 ? anx / l : 0, nz: l > 1e-9 ? anz / l : 0 };
  }

  /**
   * @param {number} x @param {number} z @param {number} [r=0]
   * @returns {boolean} True if a circle of radius `r` at (x,z) overlaps anything.
   */
  blocked(x, z, r = 0) {
    const ids = this._query(x - r, z - r, x + r, z + r);
    const s = this.shapes;
    for (let k = 0; k < ids.length; k++) {
      const id = ids[k];
      if (s.kind[id] === KIND_AABB) {
        const cx = x < s.a[id] ? s.a[id] : x > s.c[id] ? s.c[id] : x;
        const cz = z < s.b[id] ? s.b[id] : z > s.d[id] ? s.d[id] : z;
        const dx = x - cx;
        const dz = z - cz;
        if (dx * dx + dz * dz < r * r || (r === 0 && dx === 0 && dz === 0)) return true;
      } else {
        const dx = x - s.a[id];
        const dz = z - s.b[id];
        const rr = r + s.c[id];
        if (dx * dx + dz * dz < rr * rr) return true;
      }
    }
    return false;
  }

  /**
   * 2D ray march (DDA over buckets, exact shape tests inside each). Line of
   * sight for AI, muzzle traces, "can the spawner see the player" checks.
   *
   * @param {number} x @param {number} z Origin.
   * @param {number} dx @param {number} dz Direction. If it is unit length, `t`
   *   comes back in METRES; otherwise `t` is a multiple of the direction vector.
   * @param {number} [maxT=Infinity]
   * @returns {number|null} Smallest hit parameter in `[0, maxT]`, or `null`.
   */
  raycast2D(x, z, dx, dz, maxT = Infinity) {
    const len = Math.hypot(dx, dz);
    if (len < 1e-12) return null;

    // March the bucket grid, testing shapes as we enter each cell. A shape can
    // be hit before the cell that "owns" the hit point is reached, so we keep
    // the best hit and stop once the cell entry time passes it.
    const cell = this.cell;
    let cx = Math.floor((x - this.minX) / cell);
    let cz = Math.floor((z - this.minZ) / cell);
    const stepX = dx > 0 ? 1 : dx < 0 ? -1 : 0;
    const stepZ = dz > 0 ? 1 : dz < 0 ? -1 : 0;
    const tDeltaX = stepX !== 0 ? Math.abs(cell / dx) : Infinity;
    const tDeltaZ = stepZ !== 0 ? Math.abs(cell / dz) : Infinity;

    const bx = this.minX + cx * cell;
    const bz = this.minZ + cz * cell;
    let tMaxX = stepX > 0 ? (bx + cell - x) / dx : stepX < 0 ? (bx - x) / dx : Infinity;
    let tMaxZ = stepZ > 0 ? (bz + cell - z) / dz : stepZ < 0 ? (bz - z) / dz : Infinity;

    this._tick++;
    const stamp = this._stamp;
    const tick = this._tick;
    const s = this.shapes;
    let best = Infinity;
    let tEnter = 0;
    let guard = (this.nx + this.nz) * 2 + 8;

    while (guard-- > 0 && tEnter <= maxT && tEnter <= best) {
      if (cx >= 0 && cz >= 0 && cx < this.nx && cz < this.nz) {
        const list = this.buckets[cz * this.nx + cx];
        if (list) {
          for (let k = 0; k < list.length; k++) {
            const id = list[k];
            if (stamp[id] === tick) continue;
            stamp[id] = tick;
            if (s.on[id] === 0) continue;
            const t =
              s.kind[id] === KIND_AABB
                ? rayAabb(x, z, dx, dz, s.a[id], s.b[id], s.c[id], s.d[id])
                : rayCircle(x, z, dx, dz, s.a[id], s.b[id], s.c[id]);
            if (t !== null && t >= 0 && t < best) best = t;
          }
        }
      }
      if (tMaxX < tMaxZ) {
        tEnter = tMaxX;
        cx += stepX;
        tMaxX += tDeltaX;
      } else {
        tEnter = tMaxZ;
        cz += stepZ;
        tMaxZ += tDeltaZ;
      }
      if (stepX === 0 && stepZ === 0) break;
    }

    return best <= maxT && best < Infinity ? best : null;
  }

  /** @returns {{shapes: number, cells: number, occupied: number, cell: number}} */
  stats() {
    let occupied = 0;
    for (const b of this.buckets) if (b && b.length) occupied++;
    return { shapes: this.shapes.n, cells: this.nx * this.nz, occupied, cell: this.cell };
  }

  // ── internals ──────────────────────────────────────────────────────────────

  /** @returns {number} */
  _alloc() {
    const s = this.shapes;
    if (s.n === s.kind.length) {
      const cap = s.n * 2;
      const grow = (arr, Ctor) => {
        const next = new Ctor(cap);
        next.set(arr);
        return next;
      };
      s.kind = grow(s.kind, Uint8Array);
      s.a = grow(s.a, Float32Array);
      s.b = grow(s.b, Float32Array);
      s.c = grow(s.c, Float32Array);
      s.d = grow(s.d, Float32Array);
      s.y = grow(s.y, Float32Array);
      s.h = grow(s.h, Float32Array);
      s.on = grow(s.on, Uint8Array);
      s.tag.length = cap;
      const st = new Int32Array(cap);
      st.set(this._stamp);
      this._stamp = st;
    }
    s.on[s.n] = 1;
    return s.n++;
  }

  /**
   * Removes one shape id from every bucket its old AABB touched.
   * @param {number} id
   * @param {number} x0 @param {number} z0 @param {number} x1 @param {number} z1
   */
  _unbucketize(id, x0, z0, x1, z1) {
    const c = this.cell;
    const gx0 = clampI(Math.floor((x0 - this.minX) / c), 0, this.nx - 1);
    const gx1 = clampI(Math.floor((x1 - this.minX) / c), 0, this.nx - 1);
    const gz0 = clampI(Math.floor((z0 - this.minZ) / c), 0, this.nz - 1);
    const gz1 = clampI(Math.floor((z1 - this.minZ) / c), 0, this.nz - 1);
    for (let gz = gz0; gz <= gz1; gz++) {
      for (let gx = gx0; gx <= gx1; gx++) {
        const list = this.buckets[gz * this.nx + gx];
        if (!list) continue;
        const at = list.indexOf(id);
        if (at >= 0) list.splice(at, 1);
      }
    }
  }

  /**
   * @param {number} id
   * @param {number} x0 @param {number} z0 @param {number} x1 @param {number} z1
   */
  _bucketize(id, x0, z0, x1, z1) {
    const c = this.cell;
    const gx0 = clampI(Math.floor((x0 - this.minX) / c), 0, this.nx - 1);
    const gx1 = clampI(Math.floor((x1 - this.minX) / c), 0, this.nx - 1);
    const gz0 = clampI(Math.floor((z0 - this.minZ) / c), 0, this.nz - 1);
    const gz1 = clampI(Math.floor((z1 - this.minZ) / c), 0, this.nz - 1);
    for (let gz = gz0; gz <= gz1; gz++) {
      for (let gx = gx0; gx <= gx1; gx++) {
        const i = gz * this.nx + gx;
        (this.buckets[i] ||= []).push(id);
      }
    }
  }

  /**
   * Collects unique shape ids overlapping an XZ box.
   * @param {number} x0 @param {number} z0 @param {number} x1 @param {number} z1
   * @returns {number[]} REUSED scratch array — copy it if you need to keep it.
   */
  _query(x0, z0, x1, z1) {
    const out = (this._out ||= []);
    out.length = 0;
    const c = this.cell;
    const gx0 = clampI(Math.floor((x0 - this.minX) / c), 0, this.nx - 1);
    const gx1 = clampI(Math.floor((x1 - this.minX) / c), 0, this.nx - 1);
    const gz0 = clampI(Math.floor((z0 - this.minZ) / c), 0, this.nz - 1);
    const gz1 = clampI(Math.floor((z1 - this.minZ) / c), 0, this.nz - 1);
    this._tick++;
    const tick = this._tick;
    const stamp = this._stamp;
    for (let gz = gz0; gz <= gz1; gz++) {
      for (let gx = gx0; gx <= gx1; gx++) {
        const list = this.buckets[gz * this.nx + gx];
        if (!list) continue;
        for (let k = 0; k < list.length; k++) {
          const id = list[k];
          if (stamp[id] === tick) continue;
          stamp[id] = tick;
          if (this.shapes.on[id] === 0) continue;
          out.push(id);
        }
      }
    }
    return out;
  }
}

/**
 * @param {number} v @param {number} a @param {number} b
 * @returns {number}
 */
const clampI = (v, a, b) => (v < a ? a : v > b ? b : v);

/**
 * Slab test. Returns the ENTRY parameter, or 0 when the origin is inside.
 * @param {number} ox @param {number} oz @param {number} dx @param {number} dz
 * @param {number} x0 @param {number} z0 @param {number} x1 @param {number} z1
 * @returns {number|null}
 */
function rayAabb(ox, oz, dx, dz, x0, z0, x1, z1) {
  let tmin = -Infinity;
  let tmax = Infinity;
  if (dx !== 0) {
    const ta = (x0 - ox) / dx;
    const tb = (x1 - ox) / dx;
    tmin = Math.max(tmin, Math.min(ta, tb));
    tmax = Math.min(tmax, Math.max(ta, tb));
  } else if (ox < x0 || ox > x1) return null;
  if (dz !== 0) {
    const ta = (z0 - oz) / dz;
    const tb = (z1 - oz) / dz;
    tmin = Math.max(tmin, Math.min(ta, tb));
    tmax = Math.min(tmax, Math.max(ta, tb));
  } else if (oz < z0 || oz > z1) return null;
  if (tmax < 0 || tmin > tmax) return null;
  return tmin < 0 ? 0 : tmin;
}

/**
 * @param {number} ox @param {number} oz @param {number} dx @param {number} dz
 * @param {number} cx @param {number} cz @param {number} r
 * @returns {number|null}
 */
function rayCircle(ox, oz, dx, dz, cx, cz, r) {
  const mx = ox - cx;
  const mz = oz - cz;
  const a = dx * dx + dz * dz;
  const b = 2 * (mx * dx + mz * dz);
  const c = mx * mx + mz * mz - r * r;
  if (c <= 0) return 0; // origin inside
  const disc = b * b - 4 * a * c;
  if (disc < 0) return null;
  const sq = Math.sqrt(disc);
  const t0 = (-b - sq) / (2 * a);
  const t1 = (-b + sq) / (2 * a);
  if (t0 >= 0) return t0;
  if (t1 >= 0) return t1;
  return null;
}
